<template>
    <billing-information v-model="form" :showAddressFields="collectsBillingAddress">
        <template v-slot:header>Update Payment Method</template>
        <template v-if="team.card_last_four" v-slot:headerRight>
            <div class="flex items-center">
                <font-awesome-icon widthAuto :icon="cardIcon"></font-awesome-icon>
                <span class="text-sm">***************</span><span class="text-sm">{{ team.card_last_four }}</span>
            </div>
        </template>

        <template v-slot:alerts>
            <vx-alert v-if="form.successful" color="success" :closeable="false"> Your card has been updated. </vx-alert>

            <vx-alert v-if="form.errors.has('form')" color="danger" :closeable="false">
                We had trouble updating your card. It's possible your card provider is preventing us from charging the
                card. Please contact your card provider or customer support.
            </vx-alert>
        </template>

        <template v-slot:card>
            <div>
                <vx-label size="md"> Cardholder's Name </vx-label>

                <vx-input
                    size="lg"
                    v-model="cardForm.name"
                    :error="displayFirstError(validate.cardForm.name.$errors) || cardForm.errors.get('name')"
                ></vx-input>
            </div>

            <div>
                <vx-label size="md"> Card </vx-label>

                <div
                    id="payment-card-element"
                    class="block w-full appearance-none border border-solid bg-white font-sans text-slate-900 transition focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 text-base rounded-xl h-12 p-4"
                    :class="{
                        'border-rose-300': cardForm.errors.has('card'),
                        'border-slate-300': !cardForm.errors.has('card'),
                    }"
                ></div>

                <span class="mt-2 px-0.5 text-xs text-rose-800" v-show="cardForm.errors.has('card')">
                    {{ cardForm.errors.get('card') }}
                </span>
            </div>
        </template>
        <template v-slot:actions>
            <vx-button size="lg" @click="update" class="mt-3" :loading="form.busy">Update</vx-button>
        </template>
    </billing-information>
</template>

<script>
import {
    faCcAmex,
    faCcDinersClub,
    faCcDiscover,
    faCcJcb,
    faCcMastercard,
    faCcStripe,
    faCcVisa,
} from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxInput, VxLabel } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import BillingInformation from '~/components/general/BillingInformation.vue';
import axios from '~/services/axios';
import { catchRequestError } from '~/utils/request';
import { displayFirstError } from '~/utils/validation';

export default {
    components: {
        FontAwesomeIcon,
        VxAlert,
        VxLabel,
        VxButton,
        VxInput,
        BillingInformation,
    },
    props: {
        team: {
            required: true,
        },
        collectsBillingAddress: {
            required: true,
        },
    },
    setup() {
        return {
            validate: useVuelidate(),
        };
    },
    validations() {
        return {
            cardForm: {
                name: {
                    required,
                },
            },
        };
    },
    data() {
        return {
            cardElement: null,

            form: new SparkForm({
                stripe_payment_method: '',
                address: '',
                address_line_2: '',
                city: '',
                state: '',
                zip: '',
                country: 'US',
            }),

            cardForm: new SparkForm({
                name: '',
            }),
            cardElementValidation: {
                empty: true,
                complete: false,
                error: undefined,
            },
            stripe: Spark.stripeKey
                ? Stripe(Spark.stripeKey, {
                      apiVersion: Spark.stripeApiVersion,
                  })
                : null,
        };
    },
    mounted() {
        this.cardElement = this.createCardElement('#payment-card-element');

        this.initializeBillingAddress();

        Bus.$on(`sparkFormUpdated.${this.form.sparkFormId}`, this.$forceUpdate);
        Bus.$on(`sparkFormUpdated.${this.cardForm.sparkFormId}`, this.$forceUpdate);
    },
    methods: {
        displayFirstError,
        /**
         * Create a Stripe Card Element.
         */
        createCardElement(container) {
            if (!this.stripe) {
                throw 'Invalid Stripe Key/Secret';
            }

            const card = this.stripe.elements().create('card', {
                hideIcon: true,
                hidePostalCode: true,
            });

            card.mount(container);

            card.on('change', (event) => {
                this.cardElementValidation = {
                    ...this.cardElementValidation,
                    ...event,
                };
            });

            return card;
        },

        async generateToken() {
            return await (
                await axios.get('/stripe/token')
            ).data?.clientSecret;
        },
        /**
         * Initialize the billing address form for the team.
         */
        initializeBillingAddress() {
            if (!Spark.collectsBillingAddress) {
                return;
            }

            this.form.company_legal_name = this.team.billing_company_legal_name;
            this.form.address = this.team.billing_address;
            this.form.address_line_2 = this.team.billing_address_line_2;
            this.form.city = this.team.billing_city;
            this.form.state = this.team.billing_state;
            this.form.zip = this.team.billing_zip;
            this.form.country = this.team.billing_country || 'US';
        },

        /**
         * Update the team's card information.
         */
        async update() {
            if (this.form.busy) {
                return;
            }

            this.form.busy = true;
            this.form.errors.forget();
            this.form.successful = false;
            this.cardForm.errors.forget();
            this.validate.$reset();

            const isCardElementValid =
                !this.cardElementValidation.empty &&
                this.cardElementValidation.complete &&
                !this.cardElementValidation.error?.message;

            if (!(await this.validate.$validate()) || !isCardElementValid) {
                this.form.busy = false;
                this.form.successful = false;

                if (!isCardElementValid) {
                    this.cardForm.errors.set({
                        card: [this.cardElementValidation.error?.message || 'Your card information is incomplete.'],
                    });
                }

                return;
            }

            const payload = {
                name: this.cardForm.name,
                address: {
                    line1: this.form.address || '',
                    line2: this.form.address_line_2 || '',
                    city: this.form.city || '',
                    state: this.form.state || '',
                    postal_code: this.form.zip || '',
                    country: this.form.country || '',
                },
            };

            try {
                const secret = await this.generateToken();
                const stripeResponse = await this.stripe.handleCardSetup(secret, this.cardElement, {
                    payment_method_data: {
                        billing_details: payload,
                    },
                });

                if (stripeResponse.error) {
                    this.cardForm.errors.set({ card: [stripeResponse.error.message] });
                } else {
                    await this.sendUpdateToServer(stripeResponse.setupIntent.payment_method);
                    this.validate.$reset();
                }
            } catch (e) {
                this.form.errors.set({ form: [catchRequestError(e)] });
            } finally {
                this.form.busy = false;
            }
        },

        /**
         * Send the credit card update information to the server.
         */
        sendUpdateToServer(paymentMethod) {
            this.form.stripe_payment_method = paymentMethod;

            return Spark.put(this.urlForUpdate, this.form).then(() => {
                Bus.$emit('updateTeam');

                this.cardForm.name = '';
                this.cardForm.number = '';
                this.cardForm.cvc = '';
                this.cardForm.month = '';
                this.cardForm.year = '';

                if (!Spark.collectsBillingAddress) {
                    this.form.zip = '';
                }
            });
        },
    },
    computed: {
        spark() {
            return window.Spark;
        },

        /**
         * Get the URL for the payment method update.
         */
        urlForUpdate() {
            return `/settings/teams/${this.team.id}/payment-method`;
        },

        /**
         * Get the proper brand icon for the customer's credit card.
         */
        cardIcon() {
            if (!this.team.card_brand) {
                return faCcStripe;
            }

            switch (this.team.card_brand) {
                case 'amex':
                    return faCcAmex;
                case 'diners':
                    return faCcDinersClub;
                case 'discover':
                    return faCcDiscover;
                case 'jcb':
                    return faCcJcb;
                case 'mastercard':
                    return faCcMastercard;
                case 'visa':
                    return faCcVisa;
                default:
                    return faCcStripe;
            }
        },
    },
};
</script>
